import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Smile, Paperclip, X, Reply, Users, Settings } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import { useSharedCart } from '@/components/shared-cart/SharedCartProvider';
import { useUserStore } from '@/stores/userStore';
import { useUserActivity } from '@/components/shared-cart/UserPresence';

interface ChatInterfaceProps {
  sessionId: string;
  className?: string;
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  sessionId,
  className = '',
  isMinimized = false,
  onToggleMinimize,
}) => {
  const { sendMessage, onlineParticipants, sharedCart, isConnected, addMessageListener } = useSharedCart();
  const { startTyping, stopTyping } = useUserActivity();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isTyping, setIsTyping] = useState<Set<string>>(new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { user } = useUserStore();
  const currentUserId = user?.id || 'guest';
  const currentUserName = user?.displayName || 'Guest';

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle typing indicators
  const handleTypingStart = useCallback(() => {
    startTyping('chat');
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping();
    }, 3000);
  }, [startTyping, stopTyping]);

  const handleTypingStop = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    stopTyping();
  }, [stopTyping]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (value.trim() && !typingTimeoutRef.current) {
      handleTypingStart();
    } else if (!value.trim()) {
      handleTypingStop();
    }
  };

  // Send message
  const sendChatMessage = useCallback((content: string, type: ChatMessage['type'] = 'text', metadata?: any) => {
    if (!content.trim() && type === 'text') return;

    const message: ChatMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      senderId: currentUserId,
      senderName: currentUserName,
      content: content.trim(),
      type,
      timestamp: new Date(),
      metadata,
      replyTo: replyingTo?.id,
      reactions: [],
    };

    // Add to local messages
    setMessages(prev => [...prev, message]);

    // Send via WebSocket
    sendMessage('chat_message', {
      message,
      sessionId,
    });

    // Clear input and reply
    setInputValue('');
    setReplyingTo(null);
    handleTypingStop();

    // Focus input
    inputRef.current?.focus();
  }, [currentUserId, sessionId, sendMessage, replyingTo, handleTypingStop]);

  // Receive chat events from other participants
  useEffect(() => {
    const unsubscribe = addMessageListener((wsMessage) => {
      switch (wsMessage.type) {
        case 'chat_message': {
          const incoming = wsMessage.data?.message;
          if (incoming && incoming.senderId !== currentUserId) {
            setMessages(prev => [...prev, { ...incoming, timestamp: new Date(incoming.timestamp) }]);
            setUnreadCount(prev => prev + 1);
          }
          break;
        }
        case 'typing_start':
          if (wsMessage.userId && wsMessage.userId !== currentUserId) {
            setIsTyping(prev => new Set([...prev, wsMessage.userId!]));
          }
          break;
        case 'typing_stop':
          if (wsMessage.userId) {
            setIsTyping(prev => {
              const next = new Set(prev);
              next.delete(wsMessage.userId!);
              return next;
            });
          }
          break;
        case 'chat_delete': {
          const messageId = wsMessage.data?.messageId;
          if (messageId) {
            setMessages(prev => prev.filter(msg => msg.id !== messageId));
          }
          break;
        }
      }
    });

    return unsubscribe;
  }, [addMessageListener, currentUserId]);

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage(inputValue);
    }
  };

  // Handle message reactions
  const handleReact = useCallback((messageId: string, emoji: string) => {
    setMessages(prev => prev.map(msg => {
      if (msg.id === messageId) {
        const reactions = msg.reactions || [];
        const existingReaction = reactions.find(r => r.emoji === emoji);
        
        if (existingReaction) {
          if (existingReaction.users.includes(currentUserId)) {
            // Remove reaction
            return {
              ...msg,
              reactions: reactions.map(r => 
                r.emoji === emoji
                  ? {
                      ...r,
                      users: r.users.filter(u => u !== currentUserId),
                      count: r.count - 1,
                    }
                  : r
              ).filter(r => r.count > 0),
            };
          } else {
            // Add reaction
            return {
              ...msg,
              reactions: reactions.map(r => 
                r.emoji === emoji
                  ? {
                      ...r,
                      users: [...r.users, currentUserId],
                      count: r.count + 1,
                    }
                  : r
              ),
            };
          }
        } else {
          // New reaction
          return {
            ...msg,
            reactions: [
              ...reactions,
              {
                emoji,
                users: [currentUserId],
                count: 1,
              },
            ],
          };
        }
      }
      return msg;
    }));

    // Send reaction via WebSocket
    sendMessage('chat_reaction', {
      messageId,
      emoji,
      sessionId,
    });
  }, [currentUserId, sessionId, sendMessage]);

  // Handle message edit
  const handleEdit = useCallback((messageId: string, newContent: string) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId
        ? {
            ...msg,
            content: newContent,
            edited: true,
            editedAt: new Date(),
          }
        : msg
    ));

    sendMessage('chat_edit', {
      messageId,
      newContent,
      sessionId,
    });
  }, [sessionId, sendMessage]);

  // Handle message delete
  const handleDelete = useCallback((messageId: string) => {
    if (window.confirm('Delete this message?')) {
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      
      sendMessage('chat_delete', {
        messageId,
        sessionId,
      });
    }
  }, [sessionId, sendMessage]);

  // Handle reply
  const handleReply = useCallback((message: ChatMessage) => {
    setReplyingTo(message);
    inputRef.current?.focus();
  }, []);

  // Quick emoji reactions
  const quickEmojis = ['👍', '❤️', '😂', '🤔', '👎', '🎉', '🔥', '👏'];

  const handleQuickEmoji = (emoji: string) => {
    sendChatMessage(emoji, 'emoji', { emoji });
    setShowEmojiPicker(false);
  };

  // Mark messages as read when component is visible
  useEffect(() => {
    if (!isMinimized) {
      setUnreadCount(0);
    }
  }, [isMinimized]);

  if (isMinimized) {
    return (
      <div className={`chat-interface-minimized ${className}`}>
        <button
          onClick={onToggleMinimize}
          className="bg-blue-600 text-white px-4 py-2 rounded-t-lg shadow-lg hover:bg-blue-700 transition-all flex items-center gap-2"
        >
          <Users className="w-4 h-4" />
          <span>Chat</span>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[1.5rem] text-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className={`chat-interface bg-white rounded-lg shadow-lg flex flex-col ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-gray-600" />
          <div>
            <h3 className="font-semibold text-gray-900">Chat</h3>
            <div className="text-xs text-gray-500">
              {onlineParticipants.size} participant{onlineParticipants.size !== 1 ? 's' : ''} online
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
            <Settings className="w-4 h-4" />
          </button>
          {onToggleMinimize && (
            <button
              onClick={onToggleMinimize}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-96">
        {messages.map((message, index) => {
          const showAvatar = index === 0 || messages[index - 1].senderId !== message.senderId;
          
          return (
            <div key={message.id} className="group">
              <ChatMessage
                message={message}
                isCurrentUser={message.senderId === currentUserId}
                showAvatar={showAvatar}
                onReact={handleReact}
                onReply={handleReply}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            </div>
          );
        })}

        {/* Typing Indicators */}
        {isTyping.size > 0 && (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>
            <span>
              {Array.from(isTyping).join(', ')} 
              {isTyping.size === 1 ? ' is' : ' are'} typing...
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Reply Preview */}
      {replyingTo && (
        <div className="px-4 py-2 bg-gray-50 border-t border-b flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Reply className="w-4 h-4" />
            <span>Replying to {replyingTo.senderName}</span>
            <span className="text-gray-400 truncate max-w-32">
              {replyingTo.content}
            </span>
          </div>
          <button
            onClick={() => setReplyingTo(null)}
            className="p-1 hover:bg-gray-200 rounded text-gray-500"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div className="p-3 border-t bg-gray-50">
          <div className="flex flex-wrap gap-2">
            {quickEmojis.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleQuickEmoji(emoji)}
                className="p-2 hover:bg-gray-200 rounded text-lg transition-colors"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex items-end gap-3">
          <div className="flex gap-2">
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
              title="Add emoji"
            >
              <Smile className="w-5 h-5" />
            </button>
            <button
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
              title="Attach file"
            >
              <Paperclip className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={replyingTo ? `Reply to ${replyingTo.senderName}...` : "Type a message..."}
              className="w-full p-3 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={1}
              disabled={!isConnected}
            />
          </div>
          
          <button
            onClick={() => sendChatMessage(inputValue)}
            disabled={!inputValue.trim() || !isConnected}
            className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title="Send message"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>

        {!isConnected && (
          <div className="text-center text-red-500 text-sm mt-2">
            Disconnected - messages cannot be sent
          </div>
        )}
      </div>
    </div>
  );
};